import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';

import { CampaignTrackingItemType } from '../';
import { CAMPAIGN_PLATFORM } from '../generated/campaign-constants';
import type {
  CampaignDraftCreatorItem,
  CampaignDraftPostItem,
  CampaignTrackPost,
  CreatorSourceClassification,
} from '../campaign-type';

const CREATOR_HANDLE_PATTERN = /^@?[A-Za-z0-9._-]+$/;
// TikTok video IDs are 19-digit numbers; Instagram reel shortcodes are 11 characters.
const TIKTOK_POST_ID_PATTERN = /^\d{19}$/;
const INSTAGRAM_REEL_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const INVALID_CREATOR_INPUT_MESSAGE = 'Use a creator handle or a supported profile URL.';
const INVALID_CREATOR_PROFILE_URL_MESSAGE =
  'Use a supported Instagram, TikTok, or YouTube profile URL with a visible handle.';

export const CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE =
  'Currently, only TikTok and Instagram are supported for campaign tracking.';

export const CAMPAIGN_DEFAULT_PLATFORM = SOURCE_PLATFORM_TYPE.TIKTOK;

export const CAMPAIGN_SUPPORTED_PLATFORMS: readonly SOURCE_PLATFORM_TYPE[] = [
  SOURCE_PLATFORM_TYPE.TIKTOK,
  SOURCE_PLATFORM_TYPE.INSTAGRAM,
];

export function isCampaignSupportedPlatform(
  platform: string | undefined,
): platform is SOURCE_PLATFORM_TYPE {
  return Boolean(platform) && CAMPAIGN_SUPPORTED_PLATFORMS.some((item) => item === platform);
}

export function toCampaignPlatform(platform: SOURCE_PLATFORM_TYPE): CAMPAIGN_PLATFORM {
  switch (platform) {
    case SOURCE_PLATFORM_TYPE.TIKTOK:
      return CAMPAIGN_PLATFORM.TIKTOK;
    case SOURCE_PLATFORM_TYPE.INSTAGRAM:
      return CAMPAIGN_PLATFORM.INSTAGRAM;
    default:
      throw new Error(CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE);
  }
}

function normalizeText(value: string) {
  return value.trim();
}

function normalizeUsername(value: string) {
  return normalizeText(value).replace(/^@+/, '');
}

function normalizeCreatorHandle(value: string) {
  return normalizeUsername(value).toLowerCase();
}

export function formatCreatorHandleInput(handle: string) {
  return normalizeCreatorHandle(handle);
}

function normalizeUrlCandidate(value: string) {
  const trimmedValue = normalizeText(value);
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  if (/^(www\.)?(instagram|tiktok|youtube)\.com/i.test(trimmedValue)) {
    return `https://${trimmedValue.replace(/^https?:\/\//i, '')}`;
  }
  if (/^youtu\.be\//i.test(trimmedValue)) {
    return `https://${trimmedValue}`;
  }
  return trimmedValue;
}

function isPlatformHost(hostname: string, platformHost: string) {
  const normalizedHostname = hostname.toLowerCase();
  return normalizedHostname === platformHost || normalizedHostname.endsWith(`.${platformHost}`);
}

function looksLikeUrlCandidate(value: string) {
  return /^(https?:\/\/|www\.|instagram\.com|tiktok\.com|youtube\.com|youtu\.be)/i.test(
    normalizeText(value),
  );
}

export function detectPlatformFromUrl(value: string) {
  const normalizedValue = normalizeUrlCandidate(value);
  if (!normalizedValue) return undefined;

  try {
    const { hostname } = new URL(normalizedValue);
    const normalizedHost = hostname.replace(/^www\./, '').toLowerCase();

    if (normalizedHost.includes('instagram.com')) return SOURCE_PLATFORM_TYPE.INSTAGRAM;
    if (normalizedHost.includes('tiktok.com')) return SOURCE_PLATFORM_TYPE.TIKTOK;
    if (normalizedHost.includes('youtube.com') || normalizedHost.includes('youtu.be')) {
      return SOURCE_PLATFORM_TYPE.YOUTUBE;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function normalizeCampaignPostUrl(value: string): string | undefined {
  const normalizedUrlCandidate = normalizeUrlCandidate(value);
  if (!normalizedUrlCandidate) return undefined;

  try {
    const url = new URL(normalizedUrlCandidate);
    if (!['http:', 'https:'].includes(url.protocol)) return undefined;

    const pathSegments = url.pathname
      .split('/')
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (isPlatformHost(url.hostname, 'tiktok.com')) {
      const [handleSegment, videoSegment, postId] = pathSegments;
      if (
        pathSegments.length !== 3 ||
        !handleSegment?.startsWith('@') ||
        !CREATOR_HANDLE_PATTERN.test(handleSegment) ||
        videoSegment?.toLowerCase() !== 'video' ||
        !postId ||
        !TIKTOK_POST_ID_PATTERN.test(postId)
      ) {
        return undefined;
      }

      return `https://www.tiktok.com/${handleSegment}/video/${postId}`;
    }

    if (isPlatformHost(url.hostname, 'instagram.com')) {
      const [reelSegment, reelId] = pathSegments;
      if (
        pathSegments.length !== 2 ||
        !['reel', 'reels'].includes(reelSegment?.toLowerCase() ?? '') ||
        !reelId ||
        !INSTAGRAM_REEL_ID_PATTERN.test(reelId)
      ) {
        return undefined;
      }

      return `https://www.instagram.com/reel/${reelId}`;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function buildCampaignTrackPostItem(postUrl: string): CampaignTrackPost | undefined {
  const detectedPlatform = detectPlatformFromUrl(postUrl);
  if (!detectedPlatform) return undefined;

  const normalizedPostUrl = isCampaignSupportedPlatform(detectedPlatform)
    ? normalizeCampaignPostUrl(postUrl)
    : normalizeUrlCandidate(postUrl);
  if (!normalizedPostUrl) return undefined;

  return {
    type: CampaignTrackingItemType.POST,
    platform: detectedPlatform,
    post_url: normalizedPostUrl.trim(),
  };
}

function classifyCreatorProfileUrl(value: string): CreatorSourceClassification | undefined {
  const normalizedUrlCandidate = normalizeUrlCandidate(value);
  const detectedPlatform = detectPlatformFromUrl(normalizedUrlCandidate);
  if (!detectedPlatform) return undefined;

  try {
    const { pathname } = new URL(normalizedUrlCandidate);
    const segments = pathname
      .split('/')
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (detectedPlatform === SOURCE_PLATFORM_TYPE.INSTAGRAM) {
      if (segments.length !== 1) return undefined;

      const [handle] = segments;
      if (!handle || ['p', 'reel', 'reels', 'stories', 'explore', 'tv'].includes(handle)) {
        return undefined;
      }

      const normalizedHandle = normalizeCreatorHandle(handle);
      return {
        sourceType: 'profile_url',
        handle: normalizedHandle,
        normalizedHandle,
        normalizedValue: formatCreatorHandleInput(normalizedHandle),
        detectedPlatform,
      };
    }

    if (detectedPlatform === SOURCE_PLATFORM_TYPE.TIKTOK) {
      if (segments.length !== 1) return undefined;

      const [handle] = segments;
      if (!handle?.startsWith('@')) return undefined;

      const normalizedHandle = normalizeCreatorHandle(handle);
      return {
        sourceType: 'profile_url',
        handle: normalizedHandle,
        normalizedHandle,
        normalizedValue: formatCreatorHandleInput(normalizedHandle),
        detectedPlatform,
      };
    }

    if (detectedPlatform === SOURCE_PLATFORM_TYPE.YOUTUBE) {
      if (segments.length !== 1) return undefined;

      const [handle] = segments;
      if (!handle?.startsWith('@')) return undefined;

      const normalizedHandle = normalizeCreatorHandle(handle);
      return {
        sourceType: 'profile_url',
        handle: normalizedHandle,
        normalizedHandle,
        normalizedValue: formatCreatorHandleInput(normalizedHandle),
        detectedPlatform,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function classifyCreatorSource(value: string): CreatorSourceClassification | undefined {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) return undefined;

  const profileUrlClassification = classifyCreatorProfileUrl(normalizedValue);
  if (profileUrlClassification) {
    return profileUrlClassification;
  }

  if (!CREATOR_HANDLE_PATTERN.test(normalizedValue)) return undefined;

  const normalizedHandle = normalizeCreatorHandle(normalizedValue);

  return {
    sourceType: 'handle',
    handle: normalizedHandle,
    normalizedHandle,
    normalizedValue: formatCreatorHandleInput(normalizedHandle),
  };
}

export function validateCreatorInput(value: string) {
  const detectedPlatform = detectPlatformFromUrl(value);
  if (detectedPlatform && !isCampaignSupportedPlatform(detectedPlatform)) {
    return {
      classification: undefined,
      error: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
    };
  }

  const classification = classifyCreatorSource(value);
  if (classification) {
    return {
      classification,
      error: undefined,
    };
  }

  if (looksLikeUrlCandidate(value)) {
    return {
      classification: undefined,
      error: INVALID_CREATOR_PROFILE_URL_MESSAGE,
    };
  }

  return {
    classification: undefined,
    error: INVALID_CREATOR_INPUT_MESSAGE,
  };
}

export function buildCreatorDedupKey(item: CampaignDraftCreatorItem) {
  const classification = classifyCreatorSource(item.sourceValue);
  if (!classification) return undefined;

  return `creator:${classification.detectedPlatform ?? item.platform ?? 'unknown'}:${classification.normalizedHandle}`;
}

export function buildPostDedupKey(item: CampaignDraftPostItem) {
  const normalizedValue = normalizeCampaignPostUrl(item.postUrl)?.toLowerCase();
  if (!normalizedValue) return undefined;
  return `post:${normalizedValue}`;
}
